/**
 * Ready-made starting appearances (spec 50 R8/R9, ADR-0063).
 *
 * A template is a **full appearance** — sections *and* theme — that a merchant
 * picks once to start a storefront, so day one looks finished instead of blank.
 * That is a different thing from `STORE_THEME_PRESETS` in `./store-home.js`,
 * which repaints colours inside the editor and is applied over and over (spec 49
 * R12). Both exist, at different levels; neither replaces the other.
 *
 * **A constant, not a table.** Same reasoning as `defaultHomeSections`: a store
 * must never depend on data it did not create. It also lets the gallery render
 * with no round trip.
 *
 * Two properties the entries have to keep:
 *
 *  - **No image URLs.** `assertOwnImages` rejects any `imageUrl` that isn't
 *    under this company's storage prefix (spec 42 R6), so a template that
 *    shipped one would fail the very first save. Every image slot is left empty
 *    and renders the token texture. The store's own cover is the one exception —
 *    it is passed in, and it has already been ownership-checked on upload.
 *  - **No slug references.** A template cannot know a store's collections, so
 *    every CTA targets `search` or `whatsapp` and every source is
 *    `all-visible` / `newest` / `uncollected`. A dangling slug would drop its
 *    section on the storefront (spec 42 R7).
 *
 * Section ids are minted per call rather than frozen in the literal (R9): two
 * appearances built from one template must not share a section id, because ids
 * are React keys and the composer's notion of identity.
 */
import {
  STORE_THEME_PRESETS,
  DEFAULT_HEADER_CONFIG,
  DEFAULT_FOOTER_CONFIG,
  defaultStoreTheme,
  type DefaultCompositionInput,
  type HomeSection,
  type SectionCta,
  type StoreTheme,
} from "./store-home.js";

/**
 * `crypto.randomUUID` — present in Node 19+ and every browser the app supports,
 * on both sides of this module's callers (the API instantiates templates, the
 * gallery previews them).
 *
 * Declared locally because this package compiles with `lib: ["ES2023"]` and
 * `types: []` (tsconfig.base.json), so neither the DOM nor the Node global is in
 * scope. The narrow shape is deliberate: it states exactly the one method relied
 * on rather than pulling a whole lib in for it.
 */
declare const crypto: { randomUUID(): string };

const id = () => crypto.randomUUID();

/**
 * A preset by key, falling back to the default.
 *
 * `STORE_THEME_PRESETS` is a `Record<string, StoreTheme>`, so under
 * `noUncheckedIndexedAccess` every lookup is possibly-undefined. The fallback is
 * unreachable for the keys below; it exists so a renamed preset degrades to a
 * valid theme instead of failing to compile into something worse.
 */
const preset = (key: string): StoreTheme => STORE_THEME_PRESETS[key] ?? defaultStoreTheme;

/** The hero's CTA pair — catalogue always, WhatsApp only with a number to call. */
function heroCtas(input: DefaultCompositionInput): SectionCta[] {
  const ctas: SectionCta[] = [{ label: "Browse catalog", target: { kind: "search" } }];
  if (input.phone) ctas.push({ label: "WhatsApp us", target: { kind: "whatsapp" } });
  return ctas;
}

/** The opening hero every template starts from, worded per template. */
function hero(input: DefaultCompositionInput, subhead: string): HomeSection {
  return {
    id: id(),
    type: "hero_slider",
    enabled: true,
    slides: [
      {
        // The store's own cover has already passed the ownership check on
        // upload, so it is the one image a template may carry.
        ...(input.coverImageUrl ? { imageUrl: input.coverImageUrl } : {}),
        headline: input.storeName,
        subhead: input.tagline ?? subhead,
        ctas: heroCtas(input),
      },
    ],
    autoplayMs: 0,
  };
}

export interface StoreAppearanceTemplate {
  /** Stable identity, stored on `store_appearances.template_key` for display. */
  key: string;
  name: string;
  /** One line in the gallery card — what this look is *for*. */
  description: string;
  theme: StoreTheme;
  /** Fresh sections, fresh ids, on every call (R9). */
  buildSections: (input: DefaultCompositionInput) => HomeSection[];
}

/**
 * The gallery, in display order. Keyed to shipped presets so a merchant who
 * later opens the Theme tab recognises where their colours came from. (The
 * `bold` preset has no template — the pink look was cut from the gallery by
 * request; the preset itself stays, since saved themes reference it.)
 */
export const STORE_APPEARANCE_TEMPLATES: readonly StoreAppearanceTemplate[] = [
  {
    key: "spark",
    name: "Wholesale essentials",
    description: "The dependable default — your story, your collections, your catalogue.",
    theme: {
      ...preset("spark"),
      header: { ...DEFAULT_HEADER_CONFIG },
      footer: { ...DEFAULT_FOOTER_CONFIG, showContact: true },
    },
    buildSections: (input) => [
      hero(input, "Wholesale supply, built on repeat orders."),
      { id: id(), type: "stats_strip", enabled: true },
      { id: id(), type: "collection_rail", enabled: true, source: { kind: "all-visible" } },
      {
        id: id(),
        type: "product_grid",
        enabled: true,
        source: { kind: "newest" },
        limit: 8,
        heading: "New this season",
        footerLink: { label: "Browse all", target: { kind: "search" } },
      },
      { id: id(), type: "about_block", enabled: true, scheme: "muted" },
      {
        id: id(),
        type: "contact",
        enabled: true,
        heading: "Talk to us",
        body: "Ask about pricing, lead times or a sample set.",
        showWhatsapp: true,
      },
    ],
  },
  {
    key: "minimal",
    name: "Quiet catalogue",
    description: "Product first. Sparse type, no ornament — the range does the talking.",
    theme: {
      ...preset("minimal"),
      containerWidth: 1280,
      sectionSpacing: 64,
      header: { ...DEFAULT_HEADER_CONFIG, sticky: true },
      footer: { ...DEFAULT_FOOTER_CONFIG },
    },
    buildSections: (input) => [
      hero(input, "A considered range, made to order."),
      {
        id: id(),
        type: "category_grid",
        enabled: true,
        source: "collections",
        style: "circles",
        limit: 8,
        showCounts: false,
        heading: "Collections",
      },
      {
        id: id(),
        type: "product_grid",
        enabled: true,
        source: { kind: "newest" },
        limit: 8,
        heading: "Latest",
        footerLink: { label: "See everything", target: { kind: "search" } },
      },
      {
        id: id(),
        type: "rich_text",
        enabled: true,
        heading: "How we work",
        body: "Orders are placed by the pack. Tell us the sizes and quantities you need and we'll confirm availability, pricing and a dispatch date the same day.",
        align: "left",
        scheme: "muted",
      },
      { id: id(), type: "contact", enabled: true, heading: "Enquiries", showWhatsapp: true },
    ],
  },
  {
    key: "classic",
    name: "Heritage house",
    description: "For a business with years behind it — story, provenance and proof up front.",
    theme: {
      ...preset("classic"),
      header: { ...DEFAULT_HEADER_CONFIG },
      footer: { ...DEFAULT_FOOTER_CONFIG, showContact: true },
    },
    buildSections: (input) => [
      {
        id: id(),
        type: "announcement_bar",
        enabled: true,
        text: "Trusted by dealers across India",
        tone: "info",
        dismissible: true,
      },
      hero(input, "Making cloth the same careful way, season after season."),
      { id: id(), type: "about_block", enabled: true },
      { id: id(), type: "stats_strip", enabled: true, scheme: "muted" },
      { id: id(), type: "collection_rail", enabled: true, source: { kind: "all-visible" } },
      {
        id: id(),
        type: "testimonials",
        enabled: true,
        heading: "What our dealers say",
        scheme: "muted",
        items: [
          {
            quote: "Consistent quality and they have never missed a dispatch date with us.",
            author: "A long-standing dealer",
          },
          {
            quote: "Sizing is reliable across every lot, which makes reordering simple.",
            author: "A retail buyer",
          },
        ],
      },
      {
        id: id(),
        type: "contact",
        enabled: true,
        heading: "Visit or call",
        body: "We're happy to walk you through the current range.",
        showWhatsapp: true,
      },
    ],
  },
  {
    key: "midnight",
    name: "After dark",
    description: "A dark storefront that makes product photography carry the page.",
    theme: {
      ...preset("midnight"),
      sectionSpacing: 64,
      header: { ...DEFAULT_HEADER_CONFIG },
      footer: { ...DEFAULT_FOOTER_CONFIG, showContact: true, dark: true },
    },
    buildSections: (input) => [
      hero(input, "A tighter range, shot properly, priced for the trade."),
      {
        id: id(),
        type: "product_grid",
        enabled: true,
        source: { kind: "newest" },
        limit: 8,
        heading: "In stock now",
        footerLink: { label: "Browse all", target: { kind: "search" } },
      },
      {
        id: id(),
        type: "featured_product",
        enabled: true,
        eyebrow: "This season",
        headline: "The piece we're known for",
        body: "Tell buyers why this one sells — the fabric, the fit, the margin it carries.",
        cta: { label: "Ask about it", target: { kind: "whatsapp" } },
      },
      { id: id(), type: "collection_rail", enabled: true, source: { kind: "all-visible" } },
      {
        id: id(),
        type: "faq",
        enabled: true,
        heading: "Before you order",
        items: [
          {
            question: "Can I order a sample first?",
            answer: "Yes — ask us for a sample set and we'll quote it against your requirement.",
          },
          {
            question: "Do you hold stock?",
            answer: "Fast-moving packs are held in stock; the rest are made to order.",
          },
        ],
      },
      { id: id(), type: "contact", enabled: true, heading: "Reach us", showWhatsapp: true },
    ],
  },
];

/** Look a template up by key; `undefined` when the key is unknown or retired. */
export function findAppearanceTemplate(key: string): StoreAppearanceTemplate | undefined {
  return STORE_APPEARANCE_TEMPLATES.find((t) => t.key === key);
}

/** Every shipped key — the enum the create procedure validates against. */
export const STORE_APPEARANCE_TEMPLATE_KEYS = STORE_APPEARANCE_TEMPLATES.map((t) => t.key);
